import React, {useEffect, useState} from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Typography,
    Grid,
    Chip,
    Tooltip
} from '@mui/material';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, LineElement, CategoryScale, LinearScale, PointElement, Tooltip as ChartTooltip, Legend } from 'chart.js';
import {getRequest} from "../../Fetch/request";
import InfoIcon from '@mui/icons-material/Info';
import Loader from '../Loader/Loader';
import TodaysQuestion from './TodaysQuestion';
import LoginOverlay from '../Authorization/LoginOverlay';
import DSA_TOPICS from '../../Utils/DSATopics';
import SEOHead from '../SEO/SEOHead';

// Register Chart.js components
ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, ChartTooltip, Legend);

const HomePageDashboard =  ({ isAuthenticated }) => {
    const navigate = useNavigate();

    const [isDsaLoading, setIsDsaLoading] = useState(false);
    const [isCoreSubjectsLoading, setIsCoreSubjectsLoading] = useState(false);
    const [todaysQuestionStats, setTodaysQuestionStats] = useState({});
    const [currentProblem, setCurrentProblem] = useState(null);
    const [isProblemLoading, setIsProblemLoading] = useState(false);
    const [latestProblems, setLatestProblems] = useState({ lld: null, hld: null });
    const [isLatestProblemsLoading, setIsLatestProblemsLoading] = useState(false);

    // Make all API calls parallel for better performance
    const fetchAllData = async () => {
        try {
            const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
            // Note: We now allow non-authenticated users to view dashboard with default user

            // Set all loading states
            setIsDsaLoading(true);
            setIsCoreSubjectsLoading(true);
            setIsProblemLoading(true);
            setIsLatestProblemsLoading(true);

            // Make all API calls parallel
            const [
                dsaResponse,
                coreSubjectsStatsResponse,
                problemResponse,
                lldResponse,
                hldResponse
            ] = await Promise.all([
                // DSA performance
                getRequest(`${process.env.REACT_APP_URL}/performance-metrics?userId=${userId}&type=DSA`),
                // Core subjects stats - only fetch if authenticated
                isAuthenticated 
                    ? getRequest(`${process.env.REACT_APP_URL}/core-questions/stats?userId=${userId}`)
                    : Promise.resolve({ status: 'skip', message: 'Not authenticated' }),
                // Problem - only fetch if authenticated
                isAuthenticated 
                    ? getRequest(`${process.env.REACT_APP_URL}/core-questions/problem?userId=${userId}`)
                    : Promise.resolve({ status: 'skip', message: 'Not authenticated' }),
                // Latest LLD problems
                getRequest(`${process.env.REACT_APP_URL}/lld-problems?page=1&limit=1&userId=${userId}`),
                // Latest HLD problems
                getRequest(`${process.env.REACT_APP_URL}/hld-problems?page=1&limit=1&userId=${userId}`),
                // User info removed - handled in ProfilePage
                Promise.resolve(null)
            ]);

            // Process DSA response
            if (dsaResponse.status === "200" && dsaResponse.response != null) {
                setDsaPerformance(dsaResponse.response);
            }

            // Process core subjects stats response
            if (coreSubjectsStatsResponse && coreSubjectsStatsResponse.status === "200" && coreSubjectsStatsResponse.response != null) {
                const stats = {};
                if (coreSubjectsStatsResponse.response.tagStats) {
                    coreSubjectsStatsResponse.response.tagStats.forEach(tag => {
                        stats[tag.tagName] = tag.attemptedQuestions || 0;
                    });
                }
                setTodaysQuestionStats(stats);
            } else {
                setTodaysQuestionStats({});
            }

            // Process problem response
            if (problemResponse && problemResponse.status === "200" && problemResponse.response && problemResponse.response.suggestedQuestion) {
                const problem = {
                    id: problemResponse.response.suggestedQuestion.questionId,
                    question: problemResponse.response.suggestedQuestion.questionText,
                    topic: problemResponse.response.suggestedQuestion.questionTag,
                    difficulty: problemResponse.response.suggestedQuestion.difficultyLevel,
                    date: new Date().toDateString()
                };
                setCurrentProblem(problem);
            } else {
                setCurrentProblem(null);
            }

            // Process latest problems
            const latestLLD = (lldResponse?.status === '200' && Array.isArray(lldResponse.response) && lldResponse.response.length > 0) 
                ? lldResponse.response[0] 
                : null;
                
            const latestHLD = (hldResponse?.status === '200' && Array.isArray(hldResponse.response) && hldResponse.response.length > 0) 
                ? hldResponse.response[0] 
                : null;

            setLatestProblems({
                lld: latestLLD,
                hld: latestHLD
            });

            // User info is now handled in ProfilePage where it's needed

        } catch (error) {
            // Error handled silently for better performance
            setTodaysQuestionStats({});
            setCurrentProblem(null);
        } finally {
            // Clear all loading states
            setIsDsaLoading(false);
            setIsCoreSubjectsLoading(false);
            setIsProblemLoading(false);
            setIsLatestProblemsLoading(false);
        }
    };

    // Function to convert DSA topic format from API to component format
    const convertDSATopicFormat = (topicFromAPI) => {
        // Convert from "dynamic-programming" to "DYNAMIC_PROGRAMMING"
        const convertedTopic = topicFromAPI.toUpperCase().replace(/-/g, '_');
        // Ensure the topic exists in our DSA_TOPICS mapping
        return Object.keys(DSA_TOPICS).includes(convertedTopic) ? convertedTopic : 'ARRAY';
    };

    const [dsaPerformance, setDsaPerformance] = useState({
            "totalAttempt": 0,
            "totalCorrect": 0,
            "totalWrong": 0,
            "successfulSubmissionCountLastThirtyDays": [],
            "weakTopics": []
    });

    const dailyQuestionsDSA = {
        labels: Array.from({length: 30}, (_, i) => i + 1),
        datasets: [
            {
                label: '',
                data: dsaPerformance.successfulSubmissionCountLastThirtyDays,
                borderColor: '#008080',
                backgroundColor: 'rgba(0, 128, 128, 0.2)',
                tension: 0.4,
                segment: {
                    borderColor: (ctx) => {
                        const {p0, p1} = ctx;
                        return p1.parsed.y >= p0.parsed.y ? 'green' : 'red';
                    },
                },
            },
        ],
    };









    useEffect(() => {
        const fetchData = async () => {
            try {
                const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
                // Note: We now allow non-authenticated users to view dashboard

                // Set DSA loading state only
                setIsDsaLoading(true);

                // Make DSA API call only (core questions are handled by individual functions)
                const dsaResponse = await getRequest(`${process.env.REACT_APP_URL}/performance-metrics?userId=${userId}&type=DSA`);

                // Process DSA response
                if (dsaResponse.status === "200" && dsaResponse.response != null) {
                    setDsaPerformance(dsaResponse.response);
                }

            } catch (error) {
                // Error handled silently for better performance
            } finally {
                // Clear DSA loading state only
                setIsDsaLoading(false);
            }
        };

        fetchAllData();
    }, [isAuthenticated]); // Re-run when authentication state changes





    return (
        <div style={{ position: 'relative', marginTop: '20px' }}>
            <SEOHead 
                title="Practice Dashboard - Track Your Interview Preparation Progress | SlazySloth"
                description="Monitor your coding interview preparation progress. Track DSA practice, system design learning, and skill development with personalized analytics and daily challenges."
                keywords="interview preparation dashboard, coding practice tracker, DSA progress, system design learning, interview analytics, coding interview progress, technical interview preparation"
                url="https://slazysloth.com/dashboard"
                structuredData={{
                    "@context": "https://schema.org",
                    "@type": "WebApplication",
                    "name": "SlazySloth Dashboard",
                    "description": "Personal dashboard for tracking interview preparation progress",
                    "applicationCategory": "EducationalApplication",
                    "featureList": ["Progress Tracking", "Daily Challenges", "Performance Analytics", "Skill Assessment"]
                }}
            />
            
            {/* Main Dashboard Content */}
            <Container
                maxWidth="xl"
                sx={{
                    padding: '20px',
                    // Remove blur and pointer restrictions - allow everyone to see dashboard
                }}
            >

            <Grid container spacing={4} justifyContent="center">
                {/* Left Column */}
                <Grid item xs={12} md={6}>
                    <Grid container spacing={3}>
                {/* DSA Performance */}
                        <Grid item xs={12}>
                    <Card sx={{
                        boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.1)',
                        borderRadius: '15px'
                    }}>
                        <CardContent>
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                marginBottom: '10px' 
                            }}>
                                <Typography variant="h6" sx={{
                                    fontWeight: 'bold',
                                    color: '#008080',
                                }}>
                                    DSA Performance
                                </Typography>
                            </Box>
                            {isDsaLoading ? (<Loader />) : (<> 
                            {/* Inline Performance Summary */}
                            <Box display="flex" justifyContent="space-around"
                                 alignItems="center"
                                 sx={{marginBottom: '15px'}}>
                                <Box textAlign="center">
                                    <Typography variant="body2" sx={{
                                        color: '#F5A623',
                                        fontWeight: 'bold',
                                        fontSize: '14px'
                                    }}>
                                        Attempted
                                    </Typography>
                                    <Typography variant="h4" sx={{
                                        color: '#F5A623',
                                        fontWeight: 'bold',
                                        fontSize: '28px'
                                    }}>
                                        {dsaPerformance.totalAttempt}
                                    </Typography>
                                </Box>
                                <Box textAlign="center">
                                    <Typography variant="body2" sx={{
                                        color: '#008080',
                                        fontWeight: 'bold',
                                        fontSize: '14px'
                                    }}>
                                        Correct
                                    </Typography>
                                    <Typography variant="h4" sx={{
                                        color: '#008080',
                                        fontWeight: 'bold',
                                        fontSize: '28px'
                                    }}>
                                        {dsaPerformance.totalCorrect}
                                    </Typography>
                                </Box>
                                <Box textAlign="center">
                                    <Typography variant="body2" sx={{
                                        color: '#D0021B',
                                        fontWeight: 'bold',
                                        fontSize: '14px'
                                    }}>
                                        Wrong
                                    </Typography>
                                    <Typography variant="h4" sx={{
                                        color: '#D0021B',
                                        fontWeight: 'bold',
                                        fontSize: '28px'
                                    }}>
                                        {dsaPerformance.totalWrong}
                                    </Typography>
                                </Box>
                            </Box>

                            <Line data={dailyQuestionsDSA}
                                  options={{
                                      responsive: true,
                                      plugins: {
                                          legend: {
                                              labels: {
                                                  fontWeight: 'bold',
                                                  color: 'black',
                                                  boxWidth: 0  // This will hide the square box
                                              }
                                          }
                                      },
                                      scales: {
                                          y: {
                                              title: {
                                                  display: true,
                                                  text: 'Correct Submissions',
                                                  font: {
                                                      size: 12,
                                                      weight: 'bold'
                                                  }
                                              }
                                          }
                                      }
                                  }}
                                  height={90}
                                  width={250}/>
                            <Typography variant="body2" sx={{
                                marginTop: '5px',
                                color: '#666',
                                fontSize: '12px',
                                textAlign: 'center'
                            }}>
                                Past 30 days
                            </Typography>

                            {/* Weak Areas for DSA */}
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                marginTop: '10px',
                                marginBottom: '8px'
                            }}>
                                <Typography variant="body2" sx={{
                                    color: '#555',
                                    fontWeight: 'bold'
                                }}>
                                    Weak DSA Topics:
                                </Typography>
                                <Tooltip title="We identify weak topics by analyzing your attempt history and prioritizing foundational concepts you have practiced less" arrow>
                                    <InfoIcon sx={{ 
                                        fontSize: '16px', 
                                        color: '#666',
                                        cursor: 'pointer'
                                    }} />
                                </Tooltip>
                            </Box>
                            <Box display="flex" justifyContent="start" gap={2}
                                 sx={{flexWrap: 'wrap', marginBottom: '15px'}}>
                                {dsaPerformance.weakTopics.map(
                                    (topic, index) => (
                                        <Chip
                                            key={index}
                                            label={topic.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                            clickable
                                            onClick={() => navigate(
                                                "/improve-weak-areas",
                                                {state: {
                                                    topic: convertDSATopicFormat(topic)
                                                }})}
                                            sx={{
                                                backgroundColor: '#008080', // Updated to main color
                                                color: 'white',
                                                fontWeight: 'bold',
                                                padding: '8px', // Reduced padding to make it less congested
                                                fontSize: '12px',
                                                '&:hover': {
                                                    backgroundColor: '#006666', // Slightly darker shade
                                                },
                                            }}
                                        />
                                    ))}
                            </Box>

                             {/*Buttons for DSA */}
                            <Box display="flex" justifyContent="right"
                                 sx={{marginTop: '15px'}}>
                                <Tooltip title="Identify and practice weak DSA topics based on your past performance" arrow>
                                    <Button
                                        variant="contained"
                                        sx={{
                                            backgroundColor: '#B78ED5',
                                            color: '#fff',
                                            padding: '6px 20px',
                                            borderRadius: '20px',
                                            fontWeight: 'bold',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            '&:hover': {backgroundColor: '#006666'}
                                        }}
                                        onClick={() => navigate('/improve-weak-areas')}
                                    >
                                        Improve DSA
                                        <InfoIcon fontSize="small" />
                                    </Button>
                                </Tooltip>
                            </Box>
                            </>)}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Latest Problems */}
                <Grid item xs={12}>
                    <Card sx={{
                        boxShadow: 'none',
                        borderRadius: '15px'
                    }}>
                        <CardContent>
                            <Typography variant="h6" sx={{
                                fontWeight: 'bold',
                                color: '#008080',
                                marginBottom: '15px'
                            }}>
                                System Design
                            </Typography>
                            
                            {isLatestProblemsLoading ? (
                                <Loader />
                            ) : (
                                <Grid container spacing={2}>
                                    {/* LLD Problem */}
                                    <Grid item xs={12} sm={6}>
                                        <Card sx={{
                                            backgroundColor: '#f8f9fa',
                                            border: '1px solid #e9ecef',
                                            borderRadius: '8px',
                                            cursor: latestProblems.lld ? 'pointer' : 'default',
                                            transition: 'all 0.2s ease',
                                            '&:hover': latestProblems.lld ? {
                                                backgroundColor: '#e9ecef',
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                            } : {}
                                        }}
                                        onClick={() => latestProblems.lld && navigate(`/lld/problem?problemId=${latestProblems.lld.problemId}`)}
                                        >
                                            <CardContent sx={{ padding: '12px !important' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <Chip 
                                                        label="LLD" 
                                                        size="small" 
                                                        sx={{ 
                                                            backgroundColor: '#10b981', 
                                                            color: 'white',
                                                            fontWeight: 'bold',
                                                            fontSize: '10px'
                                                        }} 
                                                    />
                                                    <Typography variant="caption" sx={{ color: '#666', fontSize: '10px' }}>
                                                        {latestProblems.lld ? `Latest • ${new Date(latestProblems.lld.createdAt).toLocaleDateString()}` : 'Latest'}
                                                    </Typography>
                                                </Box>
                                                {latestProblems.lld ? (
                                                    <>
                                                        <Typography variant="body2" sx={{
                                                            fontWeight: 'bold',
                                                            color: '#333',
                                                            fontSize: '14px',
                                                            lineHeight: 1.3,
                                                            mb: 0.5
                                                        }}>
                                                            {latestProblems.lld.title}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{
                                                            color: '#666',
                                                            fontSize: '11px',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden'
                                                        }}>
                                                            {latestProblems.lld.description || 'Click to continue working on this problem'}
                                                        </Typography>
                                                    </>
                                                ) : (
                                                    <Typography variant="body2" sx={{ color: '#666', fontSize: '12px', textAlign: 'center', py: 2 }}>
                                                        No LLD problems yet. Create your first problem!
                                                    </Typography>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    {/* HLD Problem */}
                                    <Grid item xs={12} sm={6}>
                                        <Card sx={{
                                            backgroundColor: '#f8f9fa',
                                            border: '1px solid #e9ecef',
                                            borderRadius: '8px',
                                            cursor: latestProblems.hld ? 'pointer' : 'default',
                                            transition: 'all 0.2s ease',
                                            '&:hover': latestProblems.hld ? {
                                                backgroundColor: '#e9ecef',
                                                transform: 'translateY(-2px)',
                                                boxShadow: 'none'
                                            } : {}
                                        }}
                                        onClick={() => latestProblems.hld && navigate(`/hld/problem?problemId=${latestProblems.hld.problemId}`)}
                                        >
                                            <CardContent sx={{ padding: '12px !important' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <Chip 
                                                        label="HLD" 
                                                        size="small" 
                                                        sx={{ 
                                                            backgroundColor: '#3b82f6', 
                                                            color: 'white',
                                                            fontWeight: 'bold',
                                                            fontSize: '10px'
                                                        }} 
                                                    />
                                                    <Typography variant="caption" sx={{ color: '#666', fontSize: '10px' }}>
                                                        {latestProblems.hld ? `Latest • ${new Date(latestProblems.hld.createdAt).toLocaleDateString()}` : 'Latest'}
                                                    </Typography>
                                                </Box>
                                                {latestProblems.hld ? (
                                                    <>
                                                        <Typography variant="body2" sx={{
                                                            fontWeight: 'bold',
                                                            color: '#333',
                                                            fontSize: '14px',
                                                            lineHeight: 1.3,
                                                            mb: 0.5
                                                        }}>
                                                            {latestProblems.hld.title}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{
                                                            color: '#666',
                                                            fontSize: '11px',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden'
                                                        }}>
                                                            {latestProblems.hld.description || 'Click to continue working on this problem'}
                                                        </Typography>
                                                    </>
                                                ) : (
                                                    <Typography variant="body2" sx={{ color: '#666', fontSize: '12px', textAlign: 'center', py: 2 }}>
                                                        No HLD problems yet. Create your first problem!
                                                    </Typography>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                    </Grid>
                </Grid>

                {/* Right Column - All Progress */}
                <Grid item xs={12} md={6}>
                    <Grid container spacing={3}>
                        {/* Today's Question */}
                        <Grid item xs={12}>
                            <TodaysQuestion 
                                problem={currentProblem} 
                                todaysQuestionStats={todaysQuestionStats}
                                onRefreshProblem={fetchAllData}
                            />
                        </Grid>

                        {/* Today's Question Progress */}
                        <Grid item xs={12}>
                    <Card sx={{
                        boxShadow: 'none',
                        borderRadius: '15px'
                    }}>
                        <CardContent>
                            <Typography variant="h6" sx={{
                                fontWeight: 'bold',
                                color: '#008080',
                                        marginBottom: '20px'
                            }}>
                                     Core Subject Progress
                                    </Typography>

                                                                <Grid container spacing={2}>
                                {isCoreSubjectsLoading ? (
                                    <Grid item xs={12} sx={{ textAlign: 'center', py: 4 }}>
                                        <Loader />
                                    </Grid>
                                                                ) : (
                                    // Always show all 6 tags, even if some have 0 questions
                                    ['Operating Systems', 'Networking', 'DBMS', 'System Design', 'OOPs', 'Design Patterns'].map(topic => {
                                        // Get count from todaysQuestionStats or default to 0
                                        const count = todaysQuestionStats[topic] || 0;
                                        return (
                                            <Grid item xs={6} sm={4} key={topic}>
                                                <Card sx={{
                                                    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                                                    borderRadius: '12px',
                                                    border: '1px solid #e0e0e0',
                                                    textAlign: 'center',
                                                    p: 2,
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        boxShadow: '0px 4px 12px rgba(0, 128, 128, 0.2)',
                                                        transform: 'translateY(-2px)',
                                                        transition: 'all 0.2s ease'
                                                    }
                                                }}
                                                onClick={() => navigate('/past-submissions', { state: { selectedTag: topic } })}
                                                >
                                                    <Typography variant="h5" sx={{
                                                        fontWeight: 'bold',
                                                        color: '#008080',
                                                        mb: 1
                                                    }}>
                                                        {count}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{
                                                        color: '#666666',
                                                        fontWeight: '500',
                                                        display: 'block',
                                                        lineHeight: 1.2
                                                    }}>
                                                        {topic}
                                                    </Typography>
                                                </Card>
                                            </Grid>
                                        );
                                    })
                                )}
                            </Grid>
                        </CardContent>
                    </Card>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Container>
        </div>
    );
};

export default HomePageDashboard;
